import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpCircle, Brain, Building2, ChevronLeft, Home, Lock, MapPin, MapPinned, Search, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type PropertyType = "casa" | "apartamento" | "terreno" | "comercial" | "chacara" | "";

type Intent = {
  key: string;
  label: string;
  icon: ReactNode;
  description: string;
  currentType?: Exclude<PropertyType, "">;
  desiredType?: Exclude<PropertyType, "">;
  desiredSummary: string;
};

type SimulatedResult = {
  count: number;
  hasPotential: boolean;
};

const WIZARD_STORAGE_KEY = "guided-swap-teaser";
const ONBOARDING_PATH = "/app/properties/new?from=guided-simulation";

const INTENTS: Intent[] = [
  {
    key: "maior",
    label: "Trocar por imóvel maior",
    icon: <ArrowUpCircle size={22} />,
    description: "Mais espaço para sua família",
    desiredSummary: "Busco um imóvel maior, com mais espaço e melhor encaixe para a minha rotina.",
  },
  {
    key: "menor",
    label: "Trocar por imóvel menor",
    icon: <ArrowRight size={22} className="rotate-90" />,
    description: "Reduzir custos e simplificar",
    desiredSummary: "Busco um imóvel menor, mais prático e com custo total reduzido.",
  },
  {
    key: "casa_apto",
    label: "Sair de casa → apartamento",
    icon: <Building2 size={22} />,
    description: "Mais praticidade e segurança",
    currentType: "casa",
    desiredType: "apartamento",
    desiredSummary: "Quero sair de uma casa e ir para um apartamento com mais praticidade.",
  },
  {
    key: "apto_casa",
    label: "Sair de apartamento → casa",
    icon: <Home size={22} />,
    description: "Mais espaço e liberdade",
    currentType: "apartamento",
    desiredType: "casa",
    desiredSummary: "Quero sair de um apartamento e ir para uma casa com mais espaço.",
  },
  {
    key: "cidade",
    label: "Trocar de cidade",
    icon: <MapPinned size={22} />,
    description: "Nova cidade, novas oportunidades",
    desiredSummary: "Quero trocar de cidade e encontrar uma permuta alinhada ao meu novo destino.",
  },
];

const PROPERTY_TYPES: Array<{ value: Exclude<PropertyType, "">; label: string; icon: ReactNode }> = [
  { value: "casa", label: "Casa", icon: <Home size={18} /> },
  { value: "apartamento", label: "Apartamento", icon: <Building2 size={18} /> },
  { value: "terreno", label: "Terreno", icon: <MapPinned size={18} /> },
  { value: "comercial", label: "Comercial", icon: <Building2 size={18} /> },
  { value: "chacara", label: "Chácara", icon: <Home size={18} /> },
];

const LOADING_MSGS = [
  "Lendo sua intenção de troca…",
  "Cruzando valor, cidade e tipo desejado…",
  "Montando uma prévia inteligente de permuta…",
];

const formatCurrency = (raw: string) => {
  const numeric = Number(raw.replace(/\D/g, ""));
  if (!numeric) return "";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  }).format(numeric);
};

const toNumber = (raw: string) => Number(raw.replace(/\D/g, "")) || 0;

const GuidedSwapWizard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [selectedIntent, setSelectedIntent] = useState<Intent | null>(null);
  const [currentType, setCurrentType] = useState<PropertyType>("");
  const [desiredType, setDesiredType] = useState<PropertyType>("");
  const [desiredSummary, setDesiredSummary] = useState("");
  const [value, setValue] = useState("");
  const [city, setCity] = useState("");
  const [resultLoading, setResultLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(0);
  const [result, setResult] = useState<SimulatedResult | null>(null);

  const lockedCurrentType = selectedIntent?.currentType ?? "";
  const effectiveCurrentType = lockedCurrentType || currentType;

  useEffect(() => {
    if (!resultLoading) return;
    const interval = window.setInterval(() => {
      setLoadingMsg((prev) => (prev + 1) % LOADING_MSGS.length);
    }, 2100);

    return () => window.clearInterval(interval);
  }, [resultLoading]);

  const resetWizard = () => {
    setDirection(-1);
    setStep(0);
    setSelectedIntent(null);
    setCurrentType("");
    setDesiredType("");
    setDesiredSummary("");
    setValue("");
    setCity("");
    setResult(null);
    setResultLoading(false);
    setLoadingMsg(0);
  };

  const goNext = () => {
    setDirection(1);
    setStep((prev) => prev + 1);
  };

  const goBack = () => {
    setDirection(-1);
    setStep((prev) => Math.max(0, prev - 1));
  };

  const handleIntentSelect = (intent: Intent) => {
    setSelectedIntent(intent);
    setCurrentType(intent.currentType ?? "");
    setDesiredType(intent.desiredType ?? "");
    setDesiredSummary(intent.desiredSummary);
    setResult(null);
    setDirection(1);
    setStep(1);
  };

  const simulateMatch = async () => {
    setDirection(1);
    setStep(3);
    setResultLoading(true);
    setResult(null);

    try {
      const numericValue = toNumber(value);
      let minValue = numericValue * 0.7;
      let maxValue = numericValue * 1.35;

      if (selectedIntent?.key === "maior") {
        minValue = numericValue * 1.05;
        maxValue = numericValue * 1.9;
      }

      if (selectedIntent?.key === "menor") {
        minValue = numericValue * 0.45;
        maxValue = numericValue * 0.95;
      }

      let query = supabase
        .from("properties")
        .select("id", { count: "exact", head: true })
        .eq("is_active", true);

      if (desiredType) {
        query = query.eq("property_type", desiredType as "casa" | "apartamento" | "terreno" | "comercial");
      }

      if (numericValue > 0) {
        query = query.gte("estimated_value", minValue).lte("estimated_value", maxValue);
      }

      if (city.trim()) {
        query = query.ilike("city", `%${city.trim()}%`);
      }

      const { count } = await query;
      await new Promise((resolve) => window.setTimeout(resolve, user ? 1600 : 2400));

      const safeCount = count ?? 0;
      setResult({
        count: safeCount,
        hasPotential: safeCount > 0 || Boolean(desiredType || city.trim() || desiredSummary.trim()),
      });
    } catch {
      setResult({ count: 0, hasPotential: true });
    } finally {
      setResultLoading(false);
    }
  };

  const persistTeaser = () => {
    window.localStorage.setItem(
      WIZARD_STORAGE_KEY,
      JSON.stringify({
        intentKey: selectedIntent?.key,
        currentType: effectiveCurrentType,
        desiredType,
        desiredSummary,
        value,
        city,
        next: ONBOARDING_PATH,
      })
    );
  };

  const handleUnlock = (mode: "signup" | "login") => {
    persistTeaser();
    navigate(`/${mode}?next=${encodeURIComponent(ONBOARDING_PATH)}`);
  };

  const slideVariants = {
    enter: (dir: number) => ({ opacity: 0, x: dir * 36 }),
    center: { opacity: 1, x: 0 },
    exit: (dir: number) => ({ opacity: 0, x: dir * -36 }),
  };

  const canAdvanceStepOne = Boolean(value && effectiveCurrentType);
  const canSimulate = Boolean(value && desiredSummary.trim());
  const selectedIntentLabel = selectedIntent?.label ?? "";
  const currentTypeLabel = PROPERTY_TYPES.find((item) => item.value === effectiveCurrentType)?.label ?? "";
  const desiredTypeLabel = PROPERTY_TYPES.find((item) => item.value === desiredType)?.label ?? "";
  const countLabel = result?.count === 1 ? "1 imóvel compatível" : `${result?.count ?? 0} imóveis compatíveis`;

  return (
    <div className="max-w-md lg:max-w-lg overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
      <div className="border-b border-border/60 bg-muted/30 px-4 py-3">
        <div className="mb-2 flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent/10 text-accent">
            <Brain size={14} />
          </span>
          <div>
            <p className="text-xs font-display font-semibold text-foreground">Simulação inteligente de permuta</p>
            <p className="text-[10px] font-body text-muted-foreground">
              A intenção vem primeiro. Os filtros técnicos ficam por trás.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {["Intenção", "Seu imóvel", "Objetivo", "Desbloqueio"].map((label, idx) => (
            <div key={label} className="flex flex-1 items-center gap-1.5 last:flex-none">
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-display font-bold transition-all ${
                  step === idx
                    ? "bg-accent text-accent-foreground"
                    : step > idx
                      ? "bg-primary/15 text-primary"
                      : "bg-secondary text-muted-foreground"
                }`}
              >
                {idx + 1}
              </div>
              {idx < 3 && <div className="h-px flex-1 bg-border" />}
            </div>
          ))}
        </div>
      </div>

      <div className="min-h-[280px] p-4">
        <AnimatePresence mode="wait" custom={direction}>
          {step === 0 && (
            <motion.div
              key="intent"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.22 }}
              className="space-y-3"
            >
              <div>
                <h3 className="text-lg font-display font-bold text-foreground">O que você quer fazer com seu imóvel?</h3>
                <p className="mt-1 text-xs font-body text-muted-foreground">
                  Nosso algoritmo entende sua intenção, cruza valor, localização e objetivo de troca para encontrar permutas com potencial real.
                </p>
              </div>

              <div className="grid gap-2">
                {INTENTS.map((intent) => (
                  <button
                    key={intent.key}
                    type="button"
                    onClick={() => handleIntentSelect(intent)}
                    className="group flex items-center gap-2.5 rounded-xl border border-border bg-background px-3 py-2.5 text-left transition-all hover:border-accent/40 hover:bg-accent/5"
                  >
                    <span className="shrink-0 text-muted-foreground transition-colors group-hover:text-accent">{intent.icon}</span>
                    <div className="min-w-0 flex-1">
                      <span className="block text-xs font-display font-semibold text-foreground">{intent.label}</span>
                      <span className="text-[10px] font-body text-muted-foreground">{intent.description}</span>
                    </div>
                    <ArrowRight size={14} className="shrink-0 text-muted-foreground transition-colors group-hover:text-accent" />
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              key="current-property"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.22 }}
              className="space-y-4"
            >
              <button type="button" onClick={goBack} className="flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground">
                <ChevronLeft size={14} /> Voltar
              </button>

              <div>
                <h3 className="text-lg font-display font-bold text-foreground">Conte só o essencial sobre o seu imóvel</h3>
                <p className="mt-1 text-xs font-body text-muted-foreground">
                  Nada de formulário técnico agora. Só o suficiente para montar uma prévia coerente.
                </p>
              </div>

              {lockedCurrentType ? (
                <div className="rounded-xl border border-accent/15 bg-accent/5 p-3">
                  <p className="text-[10px] font-display uppercase tracking-[0.18em] text-muted-foreground">Seu imóvel atual</p>
                  <p className="mt-1 text-sm font-display font-semibold text-foreground">{currentTypeLabel}</p>
                  <p className="mt-1 text-[11px] font-body text-muted-foreground">
                    Pela sua intenção, já entendemos que você está saindo de um {currentTypeLabel.toLowerCase()}.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="text-[10px] font-display uppercase tracking-[0.18em] text-muted-foreground">
                    Qual o tipo do seu imóvel atual?
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {PROPERTY_TYPES.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setCurrentType(type.value)}
                        className={`rounded-xl border px-3 py-3 text-left transition-all ${
                          currentType === type.value
                            ? "border-accent bg-accent/5 text-accent"
                            : "border-border bg-background text-muted-foreground hover:border-accent/30"
                        }`}
                      >
                        <div className="mb-1.5">{type.icon}</div>
                        <div className="text-xs font-display font-semibold">{type.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-[10px] font-display uppercase tracking-[0.18em] text-muted-foreground">
                  Valor aproximado do seu imóvel
                </label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="R$ 650.000"
                  value={formatCurrency(value)}
                  onChange={(event) => setValue(event.target.value.replace(/\D/g, ""))}
                  className="h-11 font-display text-base font-semibold"
                />
              </div>

              <Button variant="accent" size="lg" className="w-full gap-2" disabled={!canAdvanceStepOne} onClick={goNext}>
                Continuar
                <ArrowRight size={16} />
              </Button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="desired-property"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.22 }}
              className="space-y-4"
            >
              <button type="button" onClick={goBack} className="flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground">
                <ChevronLeft size={14} /> Voltar
              </button>

              <div>
                <h3 className="text-lg font-display font-bold text-foreground">O que você busca agora?</h3>
                <p className="mt-1 text-xs font-body text-muted-foreground">
                  Já deixamos isso coerente com a sua intenção, mas você pode ajustar se quiser.
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-display uppercase tracking-[0.18em] text-muted-foreground">
                  Tipo desejado
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PROPERTY_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setDesiredType(type.value)}
                      className={`rounded-xl border px-3 py-3 text-left transition-all ${
                        desiredType === type.value
                          ? "border-accent bg-accent/5 text-accent"
                          : "border-border bg-background text-muted-foreground hover:border-accent/30"
                      }`}
                    >
                      <div className="mb-1.5">{type.icon}</div>
                      <div className="text-xs font-display font-semibold">{type.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-display uppercase tracking-[0.18em] text-muted-foreground">
                  Resumo do que você busca
                </label>
                <textarea
                  value={desiredSummary}
                  onChange={(event) => setDesiredSummary(event.target.value)}
                  placeholder="Ex: Quero uma casa em Campinas com mais espaço para a família"
                  className="min-h-[88px] w-full rounded-xl border border-border bg-background px-3 py-2.5 text-xs text-foreground outline-none transition-all placeholder:text-muted-foreground/60 focus:border-accent focus:ring-2 focus:ring-accent/20"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-display uppercase tracking-[0.18em] text-muted-foreground">
                  Cidade de interesse (opcional)
                </label>
                <div className="relative">
                  <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={city}
                    onChange={(event) => setCity(event.target.value)}
                    placeholder="Ex: Campinas"
                    className="h-11 pl-10 text-sm"
                  />
                </div>
              </div>

              <div className="rounded-xl border border-accent/15 bg-accent/5 p-3">
                <div className="flex items-start gap-2">
                  <Sparkles size={14} className="mt-0.5 shrink-0 text-accent" />
                  <div>
                    <p className="text-xs font-display font-semibold text-foreground">Direção entendida pelo sistema</p>
                    <p className="mt-1 text-[11px] font-body leading-relaxed text-muted-foreground">
                      Intenção: <span className="font-semibold text-foreground">{selectedIntentLabel}</span>
                      {effectiveCurrentType ? (
                        <>
                          {" · Você tem um "}
                          <span className="font-semibold text-foreground">{currentTypeLabel.toLowerCase()}</span>
                        </>
                      ) : null}
                      {desiredTypeLabel ? (
                        <>
                          {" · Você busca um "}
                          <span className="font-semibold text-foreground">{desiredTypeLabel.toLowerCase()}</span>
                        </>
                      ) : null}
                    </p>
                  </div>
                </div>
              </div>

              <Button variant="accent" size="lg" className="w-full gap-2" disabled={!canSimulate} onClick={simulateMatch}>
                <Search size={16} />
                Simular compatibilidade
              </Button>
              <p className="text-center text-[10px] font-body text-muted-foreground">
                Prévia inteligente sem revelar os imóveis antes do cadastro.
              </p>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="result"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.22 }}
              className="flex h-full flex-col justify-center"
            >
              {resultLoading ? (
                <div className="py-8 text-center">
                  <div className="relative mx-auto mb-4 w-fit">
                    <div className="h-12 w-12 animate-spin rounded-full border-4 border-accent/20 border-t-accent" />
                    <Brain size={18} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-accent" />
                  </div>
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={loadingMsg}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.25 }}
                      className="text-xs font-display font-semibold text-foreground"
                    >
                      {LOADING_MSGS[loadingMsg]}
                    </motion.p>
                  </AnimatePresence>
                  <p className="mt-2 text-[11px] font-body text-muted-foreground">
                    Isso é uma pré-análise guiada, não uma busca fria por filtros.
                  </p>
                </div>
              ) : user ? (
                <div className="space-y-4 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent/10 text-accent">
                    <Brain size={24} />
                  </div>

                  {result?.count ? (
                    <div className="mx-auto w-fit rounded-full border border-accent/25 bg-accent/10 px-3 py-1 text-xs font-display font-semibold text-accent">
                      {countLabel} com potencial de permuta
                    </div>
                  ) : null}

                  <div>
                    <h3 className="text-xl font-display font-bold text-foreground">
                      {result?.count ? "Sua prévia está pronta" : "Sua intenção foi entendida"}
                    </h3>
                    <p className="mt-2 text-xs font-body leading-relaxed text-muted-foreground">
                      {result?.count
                        ? "Antes de revelar os detalhes dos imóveis, complete o cadastro do seu imóvel para validar sua busca e liberar os matches certos dentro da plataforma."
                        : "Ainda não apareceu um match forte nessa prévia, mas seus critérios já estão prontos para você concluir seu cadastro e refinar a busca dentro da plataforma."}
                    </p>
                  </div>

                  <div className="rounded-xl border border-border bg-muted/30 p-3 text-left">
                    <p className="text-[10px] font-display uppercase tracking-[0.18em] text-muted-foreground">Resumo da simulação</p>
                    <div className="mt-2 space-y-1.5 text-xs font-body text-foreground">
                      <p><span className="text-muted-foreground">Seu imóvel:</span> {currentTypeLabel || "Não informado"}</p>
                      <p><span className="text-muted-foreground">Busca principal:</span> {desiredSummary}</p>
                      <p><span className="text-muted-foreground">Cidade:</span> {city.trim() || "Qualquer cidade"}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Button variant="accent" size="lg" className="w-full gap-2" onClick={() => { persistTeaser(); navigate(ONBOARDING_PATH); }}>
                      <ArrowRight size={16} />
                      Cadastrar meu imóvel e continuar
                    </Button>
                    <Button variant="outline" className="w-full" onClick={resetWizard}>
                      Refazer simulação
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent/10 text-accent">
                    <Lock size={24} />
                  </div>

                  {result?.count ? (
                    <div className="mx-auto w-fit rounded-full border border-accent/25 bg-accent/10 px-3 py-1 text-xs font-display font-semibold text-accent">
                      {countLabel} em potencial
                    </div>
                  ) : null}

                  <div>
                    <h3 className="text-xl font-display font-bold text-foreground">
                      {result?.count ? "Encontramos compatibilidade para sua busca" : "Análise concluída — resultado bloqueado"}
                    </h3>
                    <p className="mt-2 text-xs font-body leading-relaxed text-muted-foreground">
                      {result?.count
                        ? `O sistema encontrou ${countLabel}${city.trim() ? ` em ${city.trim()}` : ""}. Para proteger os proprietários e montar uma busca séria, você precisa criar sua conta e concluir o cadastro do seu imóvel antes de desbloquear os detalhes.`
                        : "Nosso algoritmo cruzou sua intenção, valor e localização com a base de imóveis. Para proteger os proprietários, os detalhes só são revelados para usuários cadastrados com o imóvel preenchido."}
                    </p>
                  </div>

                  <div className="relative overflow-hidden rounded-xl border border-border">
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70 backdrop-blur-md">
                      <div className="text-center">
                        <Lock size={18} className="mx-auto mb-1.5 text-accent" />
                        <p className="text-xs font-display font-semibold text-foreground">
                          {result?.count ? `${countLabel} encontrados` : "Imóveis compatíveis encontrados"}
                        </p>
                        <p className="text-[10px] text-muted-foreground">Crie sua conta e cadastre seu imóvel para desbloquear</p>
                      </div>
                    </div>
                    <div className="p-3 space-y-2 select-none pointer-events-none" aria-hidden="true">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-3 rounded-lg bg-muted/40 p-2.5">
                          <div className="h-10 w-10 shrink-0 rounded-lg bg-muted animate-pulse" />
                          <div className="flex-1 space-y-1">
                            <div className="h-2.5 w-3/4 rounded bg-muted animate-pulse" />
                            <div className="h-2 w-1/2 rounded bg-muted animate-pulse" />
                          </div>
                          <div className="h-5 w-14 rounded-full bg-accent/10 animate-pulse" />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-accent/15 bg-accent/5 p-3 text-left">
                    <p className="text-[10px] font-display uppercase tracking-[0.18em] text-muted-foreground">Sua simulação ficará salva</p>
                    <div className="mt-2 space-y-1.5 text-xs font-body text-foreground">
                      <p><span className="text-muted-foreground">Intenção:</span> {selectedIntentLabel}</p>
                      <p><span className="text-muted-foreground">Seu imóvel:</span> {currentTypeLabel || "Não informado"} · {formatCurrency(value) || "Valor não informado"}</p>
                      <p><span className="text-muted-foreground">Busca:</span> {desiredTypeLabel || desiredSummary}</p>
                      {city.trim() && <p><span className="text-muted-foreground">Cidade:</span> {city.trim()}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Button variant="accent" size="lg" className="w-full gap-2" onClick={() => handleUnlock("signup")}>
                      <ArrowRight size={16} />
                      Criar conta e cadastrar meu imóvel
                    </Button>
                    <Button variant="outline" className="w-full" onClick={() => handleUnlock("login")}>
                      Já tenho conta — continuar cadastro
                    </Button>
                    <Button variant="ghost" className="w-full text-xs" onClick={resetWizard}>
                      Refazer simulação
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GuidedSwapWizard;
